import { Badge } from "./Badge";
import { formatRelative, formatStatusLabel } from "../lib/format";
import { ApiRequestError } from "../types";
import type { IngestStatus } from "../types";

interface IngestPanelProps {
  status: IngestStatus | undefined;
  isLoading: boolean;
  isRefreshing: boolean;
  error: unknown;
  onRefresh: () => void;
}

function outcomeTone(outcome: string): "ok" | "warn" | "bad" | "muted" {
  if (outcome === "succeeded") return "ok";
  if (outcome === "routed") return "warn";
  if (!outcome) return "muted";
  return "bad";
}

export function IngestPanel({
  status,
  isLoading,
  isRefreshing,
  error,
  onRefresh,
}: IngestPanelProps) {
  const events = status?.recent_events ?? [];
  const submitters = status?.submitters ?? [];
  return (
    <article className="panel wide" aria-labelledby="ingestPanelTitle">
      <div className="panel-header">
        <div>
          <h2 id="ingestPanelTitle">Ingest Pipeline</h2>
          <p className="panel-subtitle">Recent terminal outcomes and submitters</p>
        </div>
        <div className="panel-header-actions">
          <Badge tone={isLoading ? "" : error ? "bad" : ""}>
            {isLoading ? "Loading" : error ? "Failed" : `${events.length} events`}
          </Badge>
          <button className="ghost" type="button" onClick={onRefresh} disabled={isRefreshing}>
            {isRefreshing ? "Refreshing" : "Refresh"}
          </button>
        </div>
      </div>
      {error ? (
        <p className="panel-error">
          {error instanceof ApiRequestError
            ? `${error.endpoint} failed: ${error.message}`
            : `Request failed: ${(error as Error).message ?? "unknown error"}`}
        </p>
      ) : null}
      {!error && events.length === 0 && submitters.length === 0 && !isLoading ? (
        <div className="empty-state">
          <p>
            <strong>No ingest activity yet.</strong>
          </p>
          <p className="muted">
            Run <code>make ingest-e2e</code> after migrations are applied to create
            terminal processing events for this panel.
          </p>
        </div>
      ) : null}
      <div className="ingest-panel-grid">
        <section aria-label="Recent ingest outcomes">
          <h3>Recent outcomes</h3>
          {events.length > 0 ? (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Time</th>
                    <th scope="col">Type</th>
                    <th scope="col">Outcome</th>
                    <th scope="col">Reason</th>
                    <th scope="col">Topic offset</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.id}>
                      <td title={event.occurred_at}>{formatRelative(event.occurred_at)}</td>
                      <td>
                        {event.type ? `${event.type} v${event.schema_version ?? "?"}` : "Decode"}
                      </td>
                      <td>
                        <Badge tone={outcomeTone(event.outcome)}>
                          {formatStatusLabel(event.outcome)}
                        </Badge>
                      </td>
                      <td className={event.reason_code ? "" : "muted"} title={event.detail ?? ""}>
                        {event.reason_code
                          ? `${formatStatusLabel(event.reason_code)}${event.detail ? `: ${event.detail}` : ""}`
                          : "None"}
                      </td>
                      <td className="mono">
                        {event.topic}:{event.partition}:{event.offset}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty-line muted">No terminal outcomes recorded.</p>
          )}
        </section>

        <section aria-label="Ingest submitters">
          <h3>Submitters</h3>
          {submitters.length > 0 ? (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Submitter</th>
                    <th scope="col">Tenant</th>
                    <th scope="col">Status</th>
                    <th scope="col">Rate tier</th>
                  </tr>
                </thead>
                <tbody>
                  {submitters.map((submitter) => (
                    <tr key={submitter.submitter_id}>
                      <td className="mono">{submitter.submitter_id}</td>
                      <td className={submitter.tenant_id ? "" : "muted"}>
                        {submitter.tenant_id ?? "Global"}
                      </td>
                      <td className={submitter.status === "active" ? "value-ok" : "value-warn"}>
                        {formatStatusLabel(submitter.status)}
                      </td>
                      <td>{formatStatusLabel(submitter.rate_limit_tier)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty-line muted">No submitters configured.</p>
          )}
        </section>
      </div>
    </article>
  );
}
